// Registers the canvas's frames-store follower (`followFramesStore`) before
// this module subscribes below, so on the canvas a frame's node is placed
// before its pending presentation flushes.
import "@/stores/canvas-store";
import { useFramesStore } from "@/stores/frames-store";
import { isWallActive } from "@/stores/workspace-view-store";
import { presentFrames } from "@/lib/canvas-presentation";
import type { PresentFramesRequest } from "@/lib/canvas-presentation-types";
import { loadPresentedFrames } from "@/lib/frame-ingest";

type PendingPresentation = {
  id: number;
  request: PresentFramesRequest;
  expiresAt: number;
  resolve: (presented: boolean) => void;
  expiryTimer: ReturnType<typeof setTimeout>;
  signal?: AbortSignal;
  onAbort?: () => void;
};

const MAX_PENDING_PRESENTATIONS = 30;
const PENDING_PRESENTATION_TTL_MS = 15_000;
let nextPendingId = 1;
const pendingPresentations: PendingPresentation[] = [];

/**
 * Every requested frame is in the store. A frame may belong to any of the
 * user's projects, whatever project the presenting run or the view is in:
 * `spaceId` names the presenter's project, never the frames'.
 */
function hasMountedTargets(request: PresentFramesRequest): boolean {
  const { framesById } = useFramesStore.getState();
  return (
    request.frameIds.length > 0 &&
    request.frameIds.every((id) => !!framesById[id])
  );
}

function settlePresentation(
  item: PendingPresentation,
  presented: boolean,
): void {
  clearTimeout(item.expiryTimer);
  if (item.signal && item.onAbort) {
    item.signal.removeEventListener("abort", item.onAbort);
  }
  item.resolve(presented);
}

function removePresentation(item: PendingPresentation, presented: boolean) {
  const index = pendingPresentations.findIndex(
    (candidate) => candidate.id === item.id,
  );
  if (index < 0) return;
  pendingPresentations.splice(index, 1);
  settlePresentation(item, presented);
}

function enqueuePresentation(
  request: PresentFramesRequest,
  signal?: AbortSignal,
): Promise<boolean> {
  const ids = request.frameIds.filter(Boolean);
  if (ids.length === 0 || signal?.aborted) return Promise.resolve(false);
  const existingIndex = pendingPresentations.findIndex(
    (item) =>
      (item.request.spaceId ?? null) === (request.spaceId ?? null) &&
      item.request.frameIds.length === ids.length &&
      item.request.frameIds.every((id, index) => ids[index] === id),
  );
  return new Promise((resolve) => {
    const pending: PendingPresentation = {
      id: nextPendingId,
      request: {
        ...request,
        frameIds: ids,
      },
      expiresAt: Date.now() + PENDING_PRESENTATION_TTL_MS,
      resolve,
      expiryTimer: undefined as unknown as ReturnType<typeof setTimeout>,
      signal,
      onAbort: undefined,
    };
    nextPendingId += 1;
    pending.expiryTimer = setTimeout(
      () => removePresentation(pending, false),
      PENDING_PRESENTATION_TTL_MS,
    );
    if (signal) {
      pending.onAbort = () => removePresentation(pending, false);
      signal.addEventListener("abort", pending.onAbort, { once: true });
    }
    if (existingIndex >= 0) {
      const replaced = pendingPresentations[existingIndex];
      if (replaced) settlePresentation(replaced, false);
      pendingPresentations.splice(existingIndex, 1, pending);
    } else {
      pendingPresentations.push(pending);
    }
    while (pendingPresentations.length > MAX_PENDING_PRESENTATIONS) {
      const evicted = pendingPresentations.shift();
      if (evicted) settlePresentation(evicted, false);
    }
  });
}

export function flushPendingFramePresentations() {
  if (pendingPresentations.length === 0) return;
  const now = Date.now();
  for (let i = pendingPresentations.length - 1; i >= 0; i -= 1) {
    const item = pendingPresentations[i];
    if (!item) continue;
    if (item.expiresAt < now) {
      pendingPresentations.splice(i, 1);
      settlePresentation(item, false);
    }
  }
  if (pendingPresentations.length === 0) return;
  const ready = pendingPresentations.filter((item) =>
    hasMountedTargets(item.request),
  );
  if (ready.length === 0) return;
  const readyIds = new Set(ready.map((item) => item.id));
  for (let i = pendingPresentations.length - 1; i >= 0; i -= 1) {
    if (readyIds.has(pendingPresentations[i]?.id ?? 0)) {
      pendingPresentations.splice(i, 1);
    }
  }
  for (const item of ready) {
    presentFrames(item.request);
    settlePresentation(item, true);
  }
}

/**
 * The wall holds only the pages it loaded, and Denker presents cards of any
 * of the user's projects, so a frame named by id (an agent's presentation)
 * may not be in the frames store yet: it is fetched by id from its own
 * project, and its arrival flushes the queue. One of another project shows
 * in Focus, never on the viewed wall. On the old canvas every frame is
 * loaded, so a missing one is still arriving.
 */
function loadUnpagedTargets(request: PresentFramesRequest): void {
  if (!isWallActive()) return;
  void loadPresentedFrames(request.frameIds);
}

export function presentFramesOrQueue(
  request: PresentFramesRequest,
  signal?: AbortSignal,
): Promise<boolean> {
  if (signal?.aborted) return Promise.resolve(false);
  if (hasMountedTargets(request)) {
    presentFrames(request);
    return Promise.resolve(true);
  }
  const queued = enqueuePresentation(request, signal);
  loadUnpagedTargets(request);
  return queued;
}

export function clearPendingFramePresentations() {
  const pending = pendingPresentations.splice(0);
  for (const item of pending) settlePresentation(item, false);
}

// A request is presented once the frames store has its frames. Frames-store
// listeners run in subscription order, and the canvas node follower
// subscribed first (import above), so canvas nodes already exist here.
useFramesStore.subscribe(() => {
  flushPendingFramePresentations();
});
